package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.FontWeight
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.RadioInput
import org.jetbrains.compose.web.dom.Span

data class RadioItem<T>(
    val value: T,
    val label: String,
    val description: String? = null,
    val disabled: Boolean = false,
)

enum class RadioGroupVariant { Inline, Card }

@Composable
fun <T> RadioGroup(
    value: T,
    items: List<RadioItem<T>>,
    onValueChange: (T) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    help: String? = null,
    error: String? = null,
    disabled: Boolean = false,
    variant: RadioGroupVariant = RadioGroupVariant.Inline,
    size: ControlSize = ControlSize.Md,
    hint: String? = null,
    required: Boolean = false,
    reserveMessageSpace: Boolean = false,
) {
    val groupName = remember { "sb-radio-${groupCounter++}" }
    FormField(
        label = label,
        modifier = modifier,
        hint = hint,
        help = help,
        error = error,
        required = required,
        reserveMessageSpace = reserveMessageSpace,
        group = true,
    ) { field ->
        Div(
            attrs = Modifier
                .styleModifier {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "${Space.s1}")
                }
                .toAttrs {
                    attr("role", "radiogroup")
                    fieldControl(field)
                },
        ) {
            items.forEach { item ->
                val itemDisabled = disabled || item.disabled
                RadioRow(
                    item = item,
                    selected = item.value == value,
                    groupName = groupName,
                    disabled = itemDisabled,
                    invalid = field.invalid,
                    variant = variant,
                    metrics = size.metrics,
                    onSelect = { onValueChange(item.value) },
                )
            }
        }
    }
}

@Composable
private fun <T> RadioRow(
    item: RadioItem<T>,
    selected: Boolean,
    groupName: String,
    disabled: Boolean,
    invalid: Boolean,
    variant: RadioGroupVariant,
    metrics: ControlMetrics,
    onSelect: () -> Unit,
) {
    val rowLook = checkRowLook(metrics, disabled)
    val look = when (variant) {
        RadioGroupVariant.Inline -> rowLook
        RadioGroupVariant.Card -> rowLook
            .classNames(ControlClass.RADIO_CARD)
            .styleModifier { property("padding", "${Space.s2} ${Space.s4}") }
    }
    Label(
        attrs = look.toAttrs {
            if (selected) attr("data-checked", "true")
            if (disabled) attr("data-disabled", "true")
        },
    ) {
        RadioInput(
            checked = selected,
            attrs = Modifier
                .classNames(ControlClass.CHECK_INPUT)
                .toAttrs {
                    name(groupName)
                    if (disabled) attr("disabled", "")
                    if (invalid) attr("aria-invalid", "true")
                    onInput { if (!disabled) onSelect() }
                },
        )
        Span(
            attrs = Modifier
                .classNames(ControlClass.RADIO_BOX)
                .width(metrics.boxPx.px)
                .height(metrics.boxPx.px)
                .toAttrs { attr("aria-hidden", "true") },
        )
        Div(
            attrs = Modifier
                .styleModifier {
                    property("display", "flex")
                    property("flex-direction", "column")
                }
                .toAttrs(),
        ) {
            SpanText(
                text = item.label,
                modifier = Modifier
                    .fontSize(metrics.fontSize)
                    .fontWeight(if (variant == RadioGroupVariant.Card && selected) FontWeight.semibold else FontWeight.regular)
                    .color(Palette.textPrimary),
            )
            item.description?.let {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textSecondary),
                )
            }
        }
    }
}

private var groupCounter = 0
